
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

export interface ShrineOffering {
  id: string;
  username: string;
  amount: number;
  created_at: string;
  profile_image_url?: string;
}

export const useShrineInteractions = () => {
  const [totalOfferings, setTotalOfferings] = useState(0);
  const [recentOfferings, setRecentOfferings] = useState<ShrineOffering[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchShrineData = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      // Fetch total offerings
      const { data: totalData, error: totalError } = await supabase
        .from('shrine_total')
        .select('total_offerings')
        .single();

      // Fetch recent offerings
      const { data: recentData, error: recentError } = await supabase
        .from('recent_shrine_offerings')
        .select('*');

      if (totalError) throw totalError;
      if (recentError) throw recentError;

      setTotalOfferings(totalData?.total_offerings || 0);
      setRecentOfferings(recentData || []);
    } catch (error) {
      console.error('Error fetching shrine data:', error);
      toast({
        title: "Error",
        description: "Could not fetch shrine data",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const contributeToShrine = async (amount: number) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to contribute",
        variant: "destructive"
      });
      return false;
    }

    try {
      const { error } = await supabase
        .from('shrine_offerings')
        .insert({
          user_id: user.id,
          amount
        });

      if (error) throw error;

      toast({
        title: "Contribution Successful",
        description: `You've contributed ${amount} aura to the Shrine`,
      });

      // Optimistically update UI
      setTotalOfferings(prev => prev + amount);
      
      // Refetch to get the most recent data
      await fetchShrineData();
      
      return true;
    } catch (error) {
      console.error('Error contributing to shrine:', error);
      toast({
        title: "Contribution Failed",
        description: "Unable to complete your offering",
        variant: "destructive"
      });
      return false;
    }
  };

  useEffect(() => {
    fetchShrineData();

    // Set up real-time subscription
    const channel = supabase
      .channel('shrine-updates')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'shrine_offerings' },
        () => fetchShrineData()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  return {
    totalOfferings,
    recentOfferings,
    isLoading,
    contributeToShrine
  };
};
